import type { ReactNode } from "react";
import { Network, RefreshCw, Target } from "lucide-react";
import type { SlideDef, SlideProps } from "@/engine/types";
import { RevealItem, RevealList, SlideShell, useAccent } from "@/ui";
import { TopicCard } from "./components/TopicCard";

function IntentChip({ dot, children }: { dot: string; children: ReactNode }) {
  return (
    <span className="flex items-center gap-2.5 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-xl font-medium text-white/85">
      <span className={`h-2.5 w-2.5 rounded-full ${dot}`} />
      {children}
    </span>
  );
}

/** Pillar page + linked deep-dive articles, drawn as a tiny hub-and-spokes graph. */
function ClusterDiagram() {
  const [from, to] = useAccent();
  const spokes = [
    { x: 28, y: 12 },
    { x: 28, y: 104 },
    { x: 248, y: 12 },
    { x: 248, y: 104 },
  ];
  return (
    <svg
      viewBox="0 0 320 144"
      className="w-full"
      role="img"
      aria-label="A pillar page linking out to four deeper articles"
    >
      <defs>
        <linearGradient id="c5-pillar-grad" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={from} stopOpacity={0.35} />
          <stop offset="100%" stopColor={to} stopOpacity={0.35} />
        </linearGradient>
      </defs>
      {spokes.map((s) => (
        <g key={`${s.x}-${s.y}`}>
          <line
            x1={160}
            y1={72}
            x2={s.x + 22}
            y2={s.y + 14}
            stroke="rgba(255,255,255,0.22)"
            strokeWidth={1.5}
          />
          <rect
            x={s.x}
            y={s.y}
            width={44}
            height={28}
            rx={8}
            fill="rgba(255,255,255,0.06)"
            stroke="rgba(255,255,255,0.18)"
          />
          <line
            x1={s.x + 9}
            y1={s.y + 11}
            x2={s.x + 35}
            y2={s.y + 11}
            stroke="rgba(255,255,255,0.35)"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
          <line
            x1={s.x + 9}
            y1={s.y + 18}
            x2={s.x + 27}
            y2={s.y + 18}
            stroke="rgba(255,255,255,0.25)"
            strokeWidth={2.5}
            strokeLinecap="round"
          />
        </g>
      ))}
      <rect
        x={102}
        y={50}
        width={116}
        height={44}
        rx={22}
        fill="url(#c5-pillar-grad)"
        stroke={from}
        strokeWidth={1.5}
      />
      <text x={160} y={80} textAnchor="middle" fontSize={20} fontWeight={600} fill="#ffffff">
        Pillar
      </text>
    </svg>
  );
}

function ContentSeoSlide({ active }: SlideProps) {
  return (
    <SlideShell chapter={5} title="Content SEO">
      <RevealList active={active} stagger={0.15} className="grid w-full grid-cols-3 gap-8">
        <RevealItem className="h-full">
          <TopicCard icon={Target} title="Match search intent">
            <div className="flex flex-wrap gap-3">
              <IntentChip dot="bg-accent-cyan">informational</IntentChip>
              <IntentChip dot="bg-accent-emerald">transactional</IntentChip>
              <IntentChip dot="bg-accent-violet">navigational</IntentChip>
            </div>
            <p className="text-2xl leading-relaxed text-white/70">
              Every query wants one of these.{" "}
              <span className="font-semibold text-white">One page, one main job</span> — when
              possible.
            </p>
          </TopicCard>
        </RevealItem>

        <RevealItem className="h-full">
          <TopicCard icon={Network} title="Topic clusters">
            <ClusterDiagram />
            <p className="text-2xl leading-relaxed text-white/70">
              A <span className="font-semibold text-white">pillar page</span> with deeper linked
              articles beats many thin near-duplicates.
            </p>
          </TopicCard>
        </RevealItem>

        <RevealItem className="h-full">
          <TopicCard icon={RefreshCw} title="Update & prune">
            <p className="text-2xl leading-relaxed text-white/70">
              <span className="font-semibold text-white">Update</span> evergreen pages when facts,
              laws, or products change.
            </p>
            <p className="text-2xl leading-relaxed text-white/70">
              <span className="font-semibold text-white">Prune or merge</span> true duplicates over
              time.
            </p>
          </TopicCard>
        </RevealItem>
      </RevealList>
    </SlideShell>
  );
}

export const contentSeo: SlideDef = {
  id: "c5-content",
  chapter: 5,
  title: "Content SEO",
  component: ContentSeoSlide,
};
